import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Microscope, Loader2, Sparkles, Target, Zap, ArrowRight } from "lucide-react";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";
import { logger } from "../lib/logger";
import { CopyBtn } from "../components/CopyBtn";
import SocialProofTicker from "../components/SocialProofTicker";
import { ShareCardActions } from "../components/ShareCardActions";

const EXAMPLES = [
  "I spent 10 years building the wrong startup so you don't have to. Here's the $2M lesson.",
  "Nobody talks about this: most 'productive' people are just anxious. Here's how I stopped.",
  "I audited 100 landing pages last month. 94 of them made the same 3 mistakes.",
];

export default function PublicAutopsyPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || "";
  const [src, setSrc] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [remaining, setRemaining] = useState(null);

  const analyze = async () => {
    setErr("");
    if (src.trim().length < 10) {
      setErr("Paste a viral post to dissect (at least one sentence).");
      return;
    }
    setLoading(true);
    setRes(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/public/autopsy`, { source_text: src });
      setRes(data.autopsy);
      setRemaining(data.remaining_today ?? null);
    } catch (e) {
      logger.error("Public autopsy error", e);
      const msg = e.response?.data?.detail;
      setErr(typeof msg === "string" ? msg : "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950" data-testid="public-autopsy-page">
      <SocialProofTicker />
      {/* Top nav */}
      <nav className="border-b border-zinc-200 dark:border-zinc-800">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-brand" strokeWidth={2.5} />
            <span className="font-black text-lg tracking-tighter text-zinc-950 dark:text-white font-heading">
              CopyForge
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-white">
              Log in
            </Link>
            <button
              onClick={() => nav(refCode ? `/register?ref=${encodeURIComponent(refCode)}` : "/register")}
              data-testid="public-autopsy-signup-btn"
              className="bg-brand text-white font-semibold px-4 py-2 text-sm hover:bg-brand-hover"
            >
              Sign up free
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-10 md:py-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-purple-500/10 text-purple-600 px-3 py-1 text-xs font-bold tracking-wider uppercase mb-4">
            <Microscope className="w-3.5 h-3.5" /> Free tool · No signup
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tighter text-zinc-950 dark:text-white font-heading leading-[1.05] mb-4">
            Why did that post<br />go viral?
          </h1>
          <p className="text-zinc-500 max-w-xl mx-auto text-lg">
            Paste any tweet, caption, or hook. We reverse-engineer the psychological template behind it —
            and write a version you can steal for your own content.
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-6 mb-8">
          <div className="md:col-span-2 space-y-3">
            <label className="text-xs font-bold tracking-wider uppercase text-zinc-400">The viral post</label>
            <textarea
              data-testid="public-autopsy-source"
              value={src}
              onChange={(e) => setSrc(e.target.value)}
              rows={8}
              maxLength={3000}
              placeholder="Paste the WORDS of a viral tweet, hook, caption, or DM. (Not a link — we can't visit URLs.)"
              className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 px-4 py-3 text-sm text-zinc-900 dark:text-white outline-none focus:border-brand resize-none"
            />
            <div className="flex flex-wrap gap-1.5">
              {EXAMPLES.map((ex, i) => (
                <button
                  key={i}
                  onClick={() => setSrc(ex)}
                  className="text-[11px] px-2 py-1 border border-zinc-200 dark:border-zinc-700 text-zinc-500 hover:border-brand hover:text-brand"
                  data-testid={`public-example-${i}`}
                >
                  Example {i + 1}
                </button>
              ))}
            </div>
            {err && <p className="text-sm text-red-500" data-testid="public-autopsy-error">{err}</p>}
            <button
              onClick={analyze}
              disabled={loading}
              data-testid="public-autopsy-run-btn"
              className="w-full bg-brand text-white font-semibold py-3.5 hover:bg-brand-hover flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Dissecting…</>) : (<><Microscope className="w-5 h-5" /> Dissect it</>)}
            </button>
            {remaining !== null && (
              <p className="text-xs text-zinc-400 text-center">
                {remaining} free dissection{remaining === 1 ? "" : "s"} left today.
                {remaining === 0 && " "}
                {remaining === 0 && (
                  <button onClick={() => nav("/register")} className="text-brand font-semibold hover:underline">
                    Unlock unlimited →
                  </button>
                )}
              </p>
            )}
          </div>

          <div className="md:col-span-3">
            {!res && !loading && (
              <div className="h-full min-h-[320px] border-2 border-dashed border-zinc-200 dark:border-zinc-800 flex items-center justify-center p-8 text-center" data-testid="public-autopsy-empty">
                <div>
                  <Microscope className="w-10 h-10 text-zinc-300 dark:text-zinc-700 mx-auto mb-3" />
                  <p className="text-sm text-zinc-400">The forensic breakdown appears here.</p>
                </div>
              </div>
            )}
            {loading && (
              <div className="h-full min-h-[320px] flex items-center justify-center">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 animate-spin text-brand mx-auto mb-3" />
                  <p className="text-sm text-zinc-500">Running viral forensics…</p>
                </div>
              </div>
            )}
            {res && (
              <div className="space-y-4" data-testid="public-autopsy-result">
                <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {res.hook_type && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-brand text-white px-2 py-1">
                        {res.hook_type}
                      </span>
                    )}
                    {res.psychological_trigger && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-purple-500 text-white px-2 py-1 flex items-center gap-1">
                        <Target className="w-3 h-3" /> {res.psychological_trigger}
                      </span>
                    )}
                  </div>
                  {res.why_it_worked && (
                    <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
                      {res.why_it_worked}
                    </p>
                  )}
                </div>

                {res.structural_template && (
                  <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold tracking-wider uppercase text-zinc-400">Reusable Template</p>
                      <CopyBtn text={res.structural_template} label="Copy" />
                    </div>
                    <pre className="whitespace-pre-wrap font-mono text-sm text-zinc-900 dark:text-zinc-100">
                      {res.structural_template}
                    </pre>
                  </div>
                )}

                {res.your_version && (
                  <div className="bg-gradient-to-br from-brand/5 to-purple-500/5 border border-brand/20 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold tracking-wider uppercase text-brand flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Your version
                      </p>
                      <CopyBtn text={res.your_version} label="Copy" />
                    </div>
                    <p className="text-sm text-zinc-900 dark:text-zinc-100 whitespace-pre-wrap leading-relaxed">
                      {res.your_version}
                    </p>
                  </div>
                )}

                <div className="bg-zinc-950 dark:bg-brand p-5 text-center" data-testid="public-autopsy-upsell">
                  <p className="text-white font-bold text-lg mb-1">
                    This one's free. Want 500 a month?
                  </p>
                  <p className="text-zinc-300 dark:text-white/80 text-sm mb-4">
                    Train your Voice Clone. Every output sounds exactly like you — forever.
                  </p>
                  <button
                    onClick={() => nav(refCode ? `/register?ref=${encodeURIComponent(refCode)}` : "/register")}
                    data-testid="public-autopsy-cta-btn"
                    className="bg-brand dark:bg-white dark:text-brand text-white font-semibold px-6 py-3 inline-flex items-center gap-2 hover:opacity-90"
                  >
                    Start my free account <ArrowRight className="w-4 h-4" />
                  </button>
                  {refCode && (
                    <p className="text-[11px] text-white/60 mt-3">Referred by {refCode}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-zinc-200 dark:border-zinc-800 pt-10 mt-12 text-center">
          <p className="text-xs font-bold tracking-wider uppercase text-zinc-400 mb-3">Also included in CopyForge</p>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-zinc-500">
            <span>Voice Clone</span>
            <span>·</span>
            <span>Trend Radar</span>
            <span>·</span>
            <span>AI copywriter (GPT + Claude)</span>
            <span>·</span>
            <span>Sora 2 video</span>
            <span>·</span>
            <span>Social toolkit</span>
          </div>
        </div>
      </main>
    </div>
  );
}
